import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Scramble from '../components/Scramble';
import LogoMarquee from '../components/LogoMarquee';
import useTitle from '../lib/useTitle';
import { CLASSES, vulns } from '../lib/vulns';
import './VulnsPage.css';

function formatMonth(iso) {
  const [y, m] = iso.split('-');
  const month = new Date(Number(y), Number(m) - 1, 1).toLocaleString('en-GB', { month: 'short' });
  return `${month} ${y}`;
}

function Evidence({ src, caption }) {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);
  return (
    <figure className="ev">
      <button type="button" className="ev__btn" onClick={() => setOpen(true)}>
        <img src={src} alt={caption || 'Evidence'} loading="lazy" />
      </button>
      {caption && <figcaption>{caption}</figcaption>}
      {open && (
        <div className="zoom" role="dialog" aria-label="Evidence preview" onClick={() => setOpen(false)}>
          <img src={src} alt={caption || 'Evidence'} />
        </div>
      )}
    </figure>
  );
}

function Entry({ v, jump }) {
  const c = CLASSES[v.cls];
  const [open, setOpen] = useState(false);
  const [inView, setInView] = useState(false);
  const [flash, setFlash] = useState(false);
  const ref = useRef(null);

  // When a logo (or any #hash link) targets this entry, open, scroll to it and
  // flash it briefly. `jump` carries a nonce so repeat clicks re-fire.
  useEffect(() => {
    if (!jump || jump.id !== v.id) return undefined;
    setOpen(true);
    setFlash(true);
    const el = ref.current;
    if (el) requestAnimationFrame(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    const t = setTimeout(() => setFlash(false), 1400);
    return () => clearTimeout(t);
  }, [jump, v.id]);

  // Reveal on scroll, kept in React state so re-renders don't clobber it.
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setInView(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          io.disconnect();
        }
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return (
    <li
      ref={ref}
      id={v.id}
      className={`tl${inView ? ' is-in' : ''}${open ? ' is-open' : ''}${flash ? ' is-target' : ''}`}
      style={{ '--vc': c.hue }}
      onMouseEnter={() => {
        // On pointer devices, hovering opens the entry and it stays open
        // until the title is clicked again.
        if (window.matchMedia('(hover: hover)').matches) setOpen(true);
      }}
    >
      <span className="tl__dot" aria-hidden="true" />
      <button
        type="button"
        className="tl__head"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <span className="tl__meta">
          <time>{formatMonth(v.date)}</time>
          <span className="tl__target">{v.target}</span>
        </span>
        <span className="tl__code">
          <Scramble text={v.title} />
        </span>
        <span className={`tl__sev tl__sev--${v.severity}`}>{v.severity}</span>
      </button>

      <div className="tl__body">
        <div className="tl__bodyinner">
          <p className="tl__realtitle">
            <span className="tl__chip" style={{ '--chip': c.hue }}>
              {c.label}
            </span>
          </p>
          <p className="tl__summary">{v.summary}</p>
          {v.outcome && <p className="tl__outcome">{v.outcome}</p>}
          {v.image && <Evidence src={v.image} caption={v.imageCaption} />}
        </div>
      </div>
    </li>
  );
}

export default function VulnsPage() {
  useTitle('Vulns');
  const { hash } = useLocation();
  const [jump, setJump] = useState(null);

  // Shared links / browser nav with a #hash jump to that report.
  useEffect(() => {
    if (hash) setJump({ id: decodeURIComponent(hash.slice(1)), n: performance.now() });
  }, [hash]);

  // Newest first.
  const ordered = useMemo(() => [...vulns].sort((a, b) => b.date.localeCompare(a.date)), []);

  return (
    <main id="content" className="vulns container">
      <header className="vulns__intro">
        <p className="vulns__kicker">security research</p>
        <h1 className="vulns__title">Vulnerabilities</h1>
        <p className="vulns__lead">
          Everything I have found and reported. Hover a line to read it, or tap a logo to jump to
          its report.
        </p>
      </header>

      <LogoMarquee onJump={(id) => setJump({ id, n: performance.now() })} />

      <ol className="timeline">
        {ordered.map((v) => (
          <Entry key={v.id} v={v} jump={jump} />
        ))}
      </ol>

      <p className="vulns__foot">
        Some reports stay sealed until the vendor ships a fix. Full disclosures live on{' '}
        <a
          className="link"
          href="https://hackerone.com/njkanade?type=user"
          target="_blank"
          rel="noopener noreferrer"
        >
          HackerOne
        </a>
        .
      </p>
    </main>
  );
}
